/**
 * guestSession, Sprint 1.7c
 *
 * Browser-local identifier for anonymous chat sessions. The chat
 * widget and order-confirm flow pass this to the edge functions so
 * unauthenticated visitors can have a real conversation + complete
 * a purchase without signing up first.
 *
 * Stored in localStorage so the id survives a page refresh within
 * the same browser. Cleared when the visitor authenticates (so a
 * subsequent guest visit gets a fresh id instead of inheriting a
 * stale one).
 *
 * Privacy: the id is opaque, it doesn't encode anything about the
 * visitor and Lembra can't reverse it to PII. It's effectively a
 * cookie-equivalent for the duration of the visit.
 */

const STORAGE_KEY = "lembra_guest_session_id";

export function getOrCreateGuestSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing && existing.length >= 8) return existing;
    const fresh = crypto.randomUUID?.() ?? `g-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // localStorage blocked (e.g. Safari private mode), fall back
    // to an in-memory id. The chat won't survive a refresh but the
    // current session works.
    return crypto.randomUUID?.() ?? `g-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function clearGuestSessionId(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore, same fallback as above.
  }
}
